import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiWithAuth from '../../services/apiWithAuth';
import { usePermissions } from '../../hooks/usePermissions';
import OnboardingStatusFilter from './OnboardingStatusFilter';

const Toast = ({ message, type, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(onClose, 5000);
        return () => clearTimeout(timer);
    }, [onClose]);

    const baseStyle = "fixed top-5 right-5 p-4 rounded-lg shadow-lg text-white flex items-center z-50";
    const typeStyles = {
        success: "bg-green-500",
        error: "bg-red-500",
        warning: "bg-yellow-500",
    };

    return (
        <div className={`${baseStyle} ${typeStyles[type] || 'bg-gray-700'}`}>
            <span>{message}</span>
            <button className="ml-4 text-xl font-bold" onClick={onClose}>×</button>
        </div>
    );
};



// --- Main StatusView Component ---

const StatusView = () => {
    const { statusType } = useParams();
    const navigate = useNavigate();
    
    // Determine module name based on status type
    const getModuleName = () => {
        if (statusType === 'accepted') return 'Accepted Applications';
        if (statusType === 'rejected') return 'Rejected Applications';
        if (statusType === 'hold') return 'Holded Applications';
        return 'Accepted Applications';
    };
    const permissions = usePermissions(getModuleName());
    
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
    const [resubmittingUsers, setResubmittingUsers] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedState, setSelectedState] = useState("");
    const [selectedCity, setSelectedCity] = useState("");
    const [selectedRole, setSelectedRole] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchUsersByStatus = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiWithAuth.get(`/users/onboarding-docs/status/${statusType}`);
            setUsers(response.data);
        } catch (err) {
            setError(`Failed to fetch ${statusType} users`);
        } finally {
            setLoading(false);
        }
    }, [statusType]);

    useEffect(() => {
        fetchUsersByStatus();
    }, [fetchUsersByStatus]);

    // Memoize dropdown options
    const { uniqueStates, uniqueCities, uniqueRoles } = useMemo(() => {
        const states = new Set();
        const cities = new Set();
        const roles = new Set();
        users.forEach(user => {
            if (user.address) {
                const parts = user.address.split(',').map(p => p.trim());
                if (parts.length > 1) {
                    cities.add(parts[parts.length - 2]);
                    states.add(parts[parts.length - 1]);
                }
            }
            if (user.userType) roles.add(user.userType);
        });
        return {
            uniqueStates: [...states].sort(),
            uniqueCities: [...cities].sort(),
            uniqueRoles: [...roles].sort()
        };
    }, [users]);


    const handleStatusChange = async (userId, newStatus, userName) => {
        try {
            await apiWithAuth.put(`/users/onboarding-docs/${userId}`, { status: newStatus });
            setUsers(prevUsers => prevUsers.filter(user => user._id !== userId));
            const statusText = newStatus === 'accepted' ? 'approved' : newStatus;
            setToast({ show: true, message: `${userName} has been ${statusText} successfully`, type: newStatus === 'accepted' ? 'success' : 'warning' });
        } catch (err) {
            setError('Failed to update user status');
            setToast({ show: true, message: 'Failed to update user status', type: 'error' });
        }
    };

    const handleResubmit = async (userId, userName) => {
        setResubmittingUsers(prev => new Set(prev).add(userId));
        try {
            await apiWithAuth.put(`/users/onboarding-docs/re-submit/${userId}?type=full`, {});
            setToast({ show: true, message: `${userName} has been requested for re-submission`, type: 'success' });
        } catch (err) {
            setToast({ show: true, message: 'Failed to request resubmission', type: 'error' });
        } finally {
            setResubmittingUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(userId);
                return newSet;
            });
        }
    };

    const filteredUsers = useMemo(() => users.filter(user => {
        const searchMatch = searchQuery ?
            [user.fullName, user.email, user.phone, user.shopName].some(field =>
                field?.toLowerCase().includes(searchQuery.toLowerCase())
            ) : true;

        const dateMatch = selectedDate ?
            new Date(user.requestDate).toLocaleDateString('en-CA') === selectedDate
            : true;

        const roleMatch = selectedRole ? user.userType === selectedRole : true;
        const address = user.address || "";
        const stateMatch = selectedState ? address.toLowerCase().includes(selectedState.toLowerCase()) : true;
        const cityMatch = selectedCity ? address.toLowerCase().includes(selectedCity.toLowerCase()) : true;

        return searchMatch && dateMatch && roleMatch && stateMatch && cityMatch;
    }), [users, searchQuery, selectedDate, selectedRole, selectedState, selectedCity]);

    // Pagination logic
    const indexOfLastUser = currentPage * rowsPerPage;
    const indexOfFirstUser = indexOfLastUser - rowsPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
    const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

    const paginate = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };


    const getStatusTitle = () => {
        return (statusType.charAt(0).toUpperCase() + statusType.slice(1) + ' Users').toUpperCase();
    };

    const getActionButtons = (user) => {
        if (!permissions.canUpdate) return null;
        
        const status = user.status?.toLowerCase();
        const buttonClass = "px-3 py-1 text-xs font-medium rounded-md text-white transition-colors";
        const actions = [];

        if (status === 'accepted') {
            actions.push(<button key="reject" className={`${buttonClass} bg-red-500 hover:bg-red-600`} onClick={() => handleStatusChange(user._id, 'rejected', user.fullName)}>Reject</button>);
            actions.push(<button key="hold" className={`${buttonClass} bg-yellow-500 hover:bg-yellow-600`} onClick={() => handleStatusChange(user._id, 'hold', user.fullName)}>Hold</button>);
        } else if (status === 'rejected') {
            actions.push(<button key="accept" className={`${buttonClass} bg-green-500 hover:bg-green-600`} onClick={() => handleStatusChange(user._id, 'accepted', user.fullName)}>Accept</button>);
            actions.push(<button key="hold" className={`${buttonClass} bg-yellow-500 hover:bg-yellow-600`} onClick={() => handleStatusChange(user._id, 'hold', user.fullName)}>Hold</button>);
            actions.push(<button key="resubmit" className={`${buttonClass} bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400`} onClick={() => handleResubmit(user.userId, user.fullName)} disabled={resubmittingUsers.has(user._id)}>{resubmittingUsers.has(user._id) ? 'Processing...' : 'Re-submit'}</button>);
        } else if (status === 'hold') {
            actions.push(<button key="accept" className={`${buttonClass} bg-green-500 hover:bg-green-600`} onClick={() => handleStatusChange(user._id, 'accepted', user.fullName)}>Accept</button>);
            actions.push(<button key="reject" className={`${buttonClass} bg-red-500 hover:bg-red-600`} onClick={() => handleStatusChange(user._id, 'rejected', user.fullName)}>Reject</button>);
        }
        return <div className="flex gap-2">{actions}</div>;
    };

    return (
        <div className="bg-gray-100 min-h-screen p-4 sm:p-6 lg:p-8">
            <style>{`
                .custom-date-input::-webkit-calendar-picker-indicator {
                    cursor: pointer;
                    filter: invert(0.6);
                }
                .custom-date-input:hover::-webkit-calendar-picker-indicator {
                    filter: invert(0.6); /* Keep filter the same on hover */
                }
            `}</style>
            {toast.show && <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />}
            <div className="max-w-7xl mx-auto">
                <header className="mb-8 text-center">
                    <h1 className="text-3xl font-bold text-gray-900">{getStatusTitle()}</h1>
                    <p className="mt-1 text-gray-600">Review and manage users based on their onboarding status.</p>
                </header>

                <div className="bg-white rounded-lg shadow-md">
                    <div className="p-4 flex flex-wrap items-center gap-2 border-b">
                        <div className="relative flex-grow min-w-[200px]">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        <OnboardingStatusFilter
                            current={['accepted', 'rejected'].includes(statusType) ? statusType : 'accepted'}
                        />
                        <select value={selectedRole} onChange={e => setSelectedRole(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500">
                            <option value="">All Roles</option>
                            {uniqueRoles.map(role => <option key={role} value={role}>{role}</option>)}
                        </select>
                        <select value={selectedState} onChange={e => setSelectedState(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500">
                            <option value="">All States</option>
                            {uniqueStates.map(state => <option key={state} value={state}>{state}</option>)}
                        </select>
                        <select value={selectedCity} onChange={e => setSelectedCity(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500">
                            <option value="">All Cities</option>
                            {uniqueCities.map(city => <option key={city} value={city}>{city}</option>)}
                        </select>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 custom-date-input"
                        />
                        <button onClick={() => { setSearchQuery(''); setSelectedDate(''); setSelectedRole(''); setSelectedState(''); setSelectedCity(''); }} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Clear</button>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-500">
                            <thead className="bg-gray-50 text-xs text-gray-700 uppercase">
                                <tr>
                                    <th scope="col" className="px-6 py-3">S.No.</th>
                                    <th scope="col" className="px-6 py-3">Name</th>
                                    <th scope="col" className="px-6 py-3">Contact</th>
                                    <th scope="col" className="px-6 py-3">Role</th>
                                    <th scope="col" className="px-6 py-3">Shop</th>
                                    <th scope="col" className="px-6 py-3">Location</th>
                                    <th scope="col" className="px-6 py-3">Request Date</th>
                                    <th scope="col" className="px-6 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="8" className="text-center py-12">Loading...</td></tr>
                                ) : error ? (
                                    <tr><td colSpan="8" className="text-center py-12 text-red-500">{error}</td></tr>
                                ) : currentUsers.length === 0 ? (
                                    <tr><td colSpan="8" className="text-center py-12">No users found.</td></tr>
                                ) : (
                                    currentUsers.map((user, index) => (
                                        <tr key={user._id} onClick={() => navigate(`/user/${user.userId}`)} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-indigo-50 cursor-pointer`}>
                                            <td className="px-6 py-4">{indexOfFirstUser + index + 1}</td>
                                            <td className="px-6 py-4 font-medium text-gray-900">
                                                {user.fullName}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div>{user.phone}</div>
                                                <div className="text-xs text-gray-500">{user.email}</div>
                                            </td>
                                            <td className="px-6 py-4">{user.userType}</td>
                                            <td className="px-6 py-4">
                                                {user.shops && user.shops.length > 0 ? (
                                                    <div>
                                                        {user.shops.map((shop, idx) => (
                                                            <div key={idx} className="mb-1">
                                                                {shop.name}
                                                                {idx < user.shops.length - 1 && <hr className="my-1" />}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    'No shop available for this user'
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {user.shops && user.shops.length > 0 ? (
                                                    <div>
                                                        {user.shops.map((shop, idx) => (
                                                            <div key={idx} className="mb-1 text-sm">
                                                                {shop.location}
                                                                {idx < user.shops.length - 1 && <hr className="my-1" />}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    'Address is not added for this user'
                                                )}
                                            </td>
                                            <td className="px-6 py-4">{new Date(user.requestDate).toLocaleString('en-GB')}</td>
                                            <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                                                {getActionButtons(user)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex items-center justify-between p-4 border-t">
                        <div>
                            <label htmlFor="rowsPerPage" className="text-sm text-gray-600 mr-2">Rows per page:</label>
                            <select
                                id="rowsPerPage"
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value));
                                    setCurrentPage(1); // Reset to first page
                                }}
                                className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                            </select>
                        </div>
                        <div className="text-sm text-gray-600">
                            Showing {filteredUsers.length > 0 ? indexOfFirstUser + 1 : 0} to {Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} results
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border rounded-md disabled:opacity-50 bg-white hover:bg-gray-100"
                            >
                                Previous
                            </button>
                            <span className="text-sm">
                                Page {currentPage} of {totalPages > 0 ? totalPages : 1}
                            </span>
                            <button
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="px-3 py-1 border rounded-md disabled:opacity-50 bg-white hover:bg-gray-100"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StatusView;